/**
 * The feature catalog: what an organization (or a resident) MAY use, and the per-org limits.
 * Layer 1 of the four permission layers (docs/PERMISSIONS.md). Shared by the Worker (worker/src/authz/) and the client
 * (nav/navConfig.ts derives each group's feature list from it), and mirrored into the accounts `features` table by
 * migration 0017 (tests/permissions.test.mts keeps the table, this file and the old navConfig rules equal).
 *
 * Defaults are per dashboard group; `platform` (the Chicago Politics team) sees the dashboard as an office does, and
 * `none` is a signed-in user without dashboard access (API users). Staff can override a default per organization in
 * `org_entitlements` (Phase 2: the admin Features tab).
 *
 * `permission` is the role layer (layer 2): the Better Auth access-control statement (auth/permissions.ts) a member's
 * role must hold to use the feature. A feature without one is open to every role the group has.
 */
export type EntitlementGroup = "resident" | "office" | "advocacy" | "platform" | "none";
export const ENTITLEMENT_GROUPS: EntitlementGroup[] = ["resident", "office", "advocacy", "platform", "none"];

export type FeatureKind = "flag" | "limit";
export type PermissionSpec = Record<string, string[]>;

export interface FeatureDef {
  kind: FeatureKind;
  /** "api": the API's own entitlements; "plan": a plan limit (auth/plans.ts). Neither is a dashboard feature (navConfig leaves them out). */
  area?: "api" | "plan";
  description: string;
  /** flag: on/off per group. limit: the number per group (null = no limit). */
  defaults: Record<EntitlementGroup, boolean | number | null>;
  /** Role layer: the statement a member's role must hold (auth/permissions.ts). */
  permission?: PermissionSpec;
  /**
   * Shows private people's data (residents, organization representatives). Layer 4 decides who may see that
   * (PRIVATE_DATA_GROUPS: offices + the platform team), so staff can never turn it on for anyone else (mayGrant).
   */
  privateData?: true;
}

const flag = (description: string, groups: ("resident" | "office" | "advocacy")[], extra: Partial<FeatureDef> = {}): FeatureDef => ({
  kind: "flag", description,
  defaults: { resident: groups.includes("resident"), office: groups.includes("office"), advocacy: groups.includes("advocacy"),
    platform: groups.includes("office"), none: false },   // the platform team sees the dashboard as an office does
  ...extra,
});
const R = "resident", O = "office", A = "advocacy";

export const FEATURES = {
  analytics:        flag("Analytics tab (dashboards, stats)", [O, A]),
  dashboards:       flag("Create, star and share Analytics dashboards inside the team", [O, A]),
  publicLinks:      flag("\"Anyone with the link\" dashboard links", [O, A], { permission: { dashboard: ["publicLink"] } }),
  organizations:    flag("Organizations tab", [R, O, A]),
  teamFollows:      flag("Team follows", [O, A]),
  teamViews:        flag("Team sidebar views", [O, A]),
  sharedNotes:      flag("Notes shared with the team", [O, A]),
  csvExport:        flag("CSV exports", [O, A]),
  access:           flag("Access: manage the team's members and invitations", [O, A], { permission: { invitation: ["create"] } }),
  orgAlertDefaults: flag("Team alert defaults", [O, A], { permission: { alertRule: ["update"] } }),
  peek:             flag("Peek", [R, O, A]),
  compare:          flag("Compare people", [R, O, A]),
  flag:             flag("Flag as inaccurate", [R, O, A]),
  myAlderperson:    flag("My alderperson", [R]),
  myStances:        flag("My stances", [R]),
  claimProfile:     flag("Claim your profile", [R]),
  personNotes:      flag("Person tags and team notes", [O], { privateData: true }),
  constituents:     flag("Constituents (testimony alerts by ward)", [O], { privateData: true }),
  targetLists:      flag("Target lists", [A]),
  briefMe:          flag("Brief me", [A]),
  alignment:        flag("Team positions / alignment", [O, A]),
  forecast:         flag("Vote forecasts", [R, O, A]),
  influence:        flag("Lobbying influence table", [R, O, A]),
  wardVoice:        flag("Ward voice totals", [O], { privateData: true }),
  whipCount:        flag("Whip count", [O]),
  yourWay:          flag("Your-way perspective switch", [A]),
  forecastAlerts:   flag("10-point forecast alerts", [A]),
  coalitions:       flag("Coalitions", [A]),
  oppositionWatch:  flag("Opposition watch", [A]),
  meetingLog:       flag("Meeting log (team-only notes)", [O]),
  // Design section 11 (accounts 0024): casework for offices (the Ward Office plan); campaigns are the Advocacy plan add-on,
  // off for every group until staff turn them on for an organization (Admin → Organizations → Features).
  casework:         flag("Casework inbox (311 requests, owners, patterns by block)", [O], { privateData: true }),
  campaigns:        flag("Campaigns and supporter action pages (Advocacy add-on)", []),
  // Plan limits (accounts 0023; auth/plans.ts, the "Plan limits in the app" design). No limit by default: today's behavior.
  seats:            { kind: "limit", area: "plan", description: "Team seats (members and pending invitations)", defaults: { resident: null, office: null, advocacy: null, platform: null, none: null } },
  briefsPerMonth:   { kind: "limit", area: "plan", description: "Brief me briefs per calendar month (UTC)", defaults: { resident: null, office: null, advocacy: null, platform: null, none: null } },
  // API (Phase 3 builds on these). Today any signed-in account may create keys (apikeys.js), residents included.
  apiAccess:        { kind: "flag", area: "api", description: "Create and use API keys", defaults: { resident: true, office: true, advocacy: true, platform: true, none: true } },
  apiDailyRequests: { kind: "limit", area: "api", description: "API requests per UTC day", defaults: { resident: 1000, office: 1000, advocacy: 1000, platform: 1000, none: 1000 } },
  apiDailyAi:       { kind: "limit", area: "api", description: "API AI answers per UTC day", defaults: { resident: 50, office: 50, advocacy: 50, platform: 50, none: 50 } },
} satisfies Record<string, FeatureDef>;

export type FeatureKey = keyof typeof FEATURES;
export const FEATURE_KEYS = Object.keys(FEATURES) as FeatureKey[];

const def = (k: FeatureKey): FeatureDef => FEATURES[k];

/** The flags a group has on by default (dashboard features only, unless `api`). */
export function defaultFlags(group: EntitlementGroup, { api = false } = {}): FeatureKey[] {
  return FEATURE_KEYS.filter((k) => def(k).kind === "flag" && def(k).defaults[group] === true && (api || def(k).area !== "api"));
}

/** The groups that may see private people's data (the privacy rule, layer 4: offices + the platform team). */
export const PRIVATE_DATA_GROUPS: readonly EntitlementGroup[] = ["office", "platform"];

/**
 * May staff turn this feature ON for an org of this group? Every feature except a privateData one outside
 * PRIVATE_DATA_GROUPS (turning a feature off, or setting a limit, is always allowed).
 */
export function mayGrant(feature: string, group: string | null | undefined): boolean {
  const d = (FEATURES as Record<string, FeatureDef>)[feature];
  return !!d && (!d.privateData || PRIVATE_DATA_GROUPS.includes(group as EntitlementGroup));
}
